import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { brandsAPI } from '../utils/api';
import { Plus, Globe, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import AIAgentStatus from '../components/AIAgentStatus';

const BrandsPage = () => {
  const navigate = useNavigate();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    website: '',
    industry: '',
    target_keywords: '',
    locations: ''
  });

  useEffect(() => {
    fetchBrands();
  }, []);

  const fetchBrands = async () => {
    try {
      const response = await brandsAPI.getAll();
      setBrands(response.data.brands);
    } catch (error) {
      toast.error('Failed to fetch brands');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await brandsAPI.create({
        ...formData,
        target_keywords: formData.target_keywords.split(',').map(k => k.trim()),
        locations: formData.locations.split(',').map(l => l.trim())
      });
      toast.success('Brand created successfully!');
      setDialogOpen(false);
      setFormData({ name: '', description: '', website: '', industry: '', target_keywords: '', locations: '' });
      fetchBrands();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create brand');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-4xl font-bold heading">Brands</h1>
            <p className="text-gray-600 mt-2">
              {JSON.parse(localStorage.getItem('user') || '{}').role === 'agency_admin' || JSON.parse(localStorage.getItem('user') || '{}').role === 'AGENCY_ADMIN'
                ? 'Manage client brands and automations'
                : 'Manage your brand automations'}
            </p>
          </div>
          <Button 
            onClick={() => {
              const user = JSON.parse(localStorage.getItem('user') || '{}');
              if (user.role === 'agency_admin' || user.role === 'AGENCY_ADMIN') {
                // White label user - go to onboarding for client brand
                navigate('/onboarding?mode=client');
              } else {
                setDialogOpen(true);
              }
            }} 
            className="btn-primary"
          >
            <Plus className="w-4 h-4 mr-2" />
            {JSON.parse(localStorage.getItem('user') || '{}').role === 'agency_admin' || JSON.parse(localStorage.getItem('user') || '{}').role === 'AGENCY_ADMIN'
              ? 'Create Client Brand'
              : 'Create Brand'}
          </Button>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Brand</DialogTitle>
                <DialogDescription>Add a new brand to start automating</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">Brand Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="website">Website</Label>
                  <Input
                    id="website"
                    type="url"
                    placeholder="https://example.com"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="industry">Industry</Label>
                  <Input
                    id="industry"
                    value={formData.industry}
                    onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="target_keywords">Target Keywords (comma separated)</Label>
                  <Input
                    id="target_keywords"
                    placeholder="seo, marketing, automation"
                    value={formData.target_keywords}
                    onChange={(e) => setFormData({ ...formData, target_keywords: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="locations">Locations (comma separated)</Label>
                  <Input
                    id="locations"
                    placeholder="Mumbai, Delhi, Bangalore"
                    value={formData.locations}
                    onChange={(e) => setFormData({ ...formData, locations: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Input
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </div>
                <Button type="submit" className="w-full">
                  Create Brand
                </Button>
              </form>
            </DialogContent>
          </Dialog>

        {brands.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {brands.map((brand) => (
              <Card key={brand.id} className="card">
                <CardHeader>
                  <CardTitle>{brand.name}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    {brand.website && (
                      <div className="flex items-center text-gray-600">
                        <Globe className="w-4 h-4 mr-2" />
                        <a href={brand.website} target="_blank" rel="noopener noreferrer" className="hover:text-blue-600">
                          {brand.website}
                        </a>
                      </div>
                    )}
                    {brand.locations && brand.locations.length > 0 && (
                      <div className="flex items-center text-gray-600">
                        <MapPin className="w-4 h-4 mr-2" />
                        {brand.locations.join(', ')}
                      </div>
                    )}
                    {brand.industry && (
                      <div className="text-gray-600">
                        <span className="font-medium">Industry:</span> {brand.industry}
                      </div>
                    )}
                  </div>
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-xs text-gray-500">Automation Status</p>
                    <div className="flex items-center mt-1">
                      <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                      <span className="text-sm font-medium text-green-600">Active</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="card text-center py-16">
            <CardContent>
              <p className="text-gray-500 mb-4">No brands yet. Create your first brand to get started!</p>
              <Button onClick={() => setDialogOpen(true)}>
                <Plus className="mr-2 h-5 w-5" />
                Create Your First Brand
              </Button>
            </CardContent>
          </Card>
        )}
        
        {/* AI Agent Status - Show for first brand if brands exist */}
        {brands.length > 0 && (
          <div className="mt-6">
            <h2 className="text-2xl font-bold mb-4">AI Agent Automation</h2>
            <AIAgentStatus brandId={brands[0].id} refreshTrigger={brands.length} />
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default BrandsPage;
